// Login experiment v2 pages (ClickUp z94kydcrz3). Loaded only by the
// *-v2-*.html pages, after main.js. main.js (local.js, header.js) still owns
// sign-in, sign-up, forgot-password and the Google modals; this file only
// adds what the new frame needs on top:
//
//   - password reveal as a real button (PRD defect 4)
//   - links that keep the query string, and carry the typed email across
//     login -> forgot password / SSO / sign up (PRD defect 7)
//   - the remembered-account chip on login
//   - the "Check your email" state on forgot password
//   - the arm A1 carousel and the arm A2 shortcut line
//   - panel and auth-method analytics
import "../../styles/login-v2.scss";
import {
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
} from "../services/utility";
import { AUTH_EVENT, trackAuth } from "../services/authEvents";
import {
  accountInitials,
  clearRememberedAccount,
  readRememberedAccount,
  writeRememberedAccount,
} from "./rememberedAccount";
import { switchShortcut } from "./osShortcut";

const CARRY_EMAIL_KEY = "sc_auth_email";
// Email carried in from another auth page, if any (see initLinks).
let carriedEmail = "";

const EYE =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>';
const EYE_OFF =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M3 3l18 18M10.6 5.1A10.4 10.4 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7c1.6 0 3-.4 4.2-1"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>';

const $ = (sel, root) => (root || document).querySelector(sel);
const $$ = (sel, root) =>
  Array.prototype.slice.call((root || document).querySelectorAll(sel));

const pageName = () => {
  const m = window.location.pathname.match(
    /(login|signup|forgot-password|sso-login)\/?$/,
  );
  return m ? m[1] : "";
};

const reducedMotion = () =>
  !!(
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

/* ---------- Password reveal ---------- */
function initReveal() {
  $$("[data-reveal]").forEach((btn) => {
    const input = document.getElementById(btn.getAttribute("data-reveal"));
    if (!input) return;
    btn.innerHTML = EYE;
    btn.addEventListener("click", () => {
      const show = input.type === "password";
      input.type = show ? "text" : "password";
      btn.setAttribute("aria-pressed", show ? "true" : "false");
      btn.setAttribute("aria-label", show ? "Hide password" : "Show password");
      btn.innerHTML = show ? EYE_OFF : EYE;
    });
  });
}

/* ---------- Links: keep the query string, carry the email ---------- */
function initLinks() {
  // Invite, redirect, TikTok and offer params must survive moving between the
  // auth pages, exactly as control's JS-only links did. These are real links
  // now (PRD defects 2 and 3), so the href itself carries the query.
  const search = window.location.search;
  if (search) {
    $$(
      "a[data-keep-query], a.create-an-account-btn, a.forgot-password",
    ).forEach((a) => {
      const href = a.getAttribute("href") || "";
      if (href.charAt(0) === "/" && href.indexOf("?") < 0) {
        a.setAttribute("href", href + search);
      }
    });
  }

  // Capture phase: local.js stops propagation on the forgot-password link.
  document.addEventListener(
    "click",
    (e) => {
      const a =
        e.target.closest &&
        e.target.closest("[data-carry-email], .create-an-account-btn");
      if (!a) return;
      const input = $("form input[name=email]");
      const email = input && input.value ? input.value.trim() : "";
      if (email) setSessionItem(CARRY_EMAIL_KEY, email);
    },
    true,
  );

  const carried = getSessionItem(CARRY_EMAIL_KEY);
  if (carried) {
    removeSessionItem(CARRY_EMAIL_KEY);
    carriedEmail = carried.trim().toLowerCase();
    const input = $("form input[name=email]");
    if (input && !input.value && carried.length <= 254) {
      input.value = carried;
      // Lets local.js re-check the sign-up button's enabled state.
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }
}

/* ---------- Remembered account (login only) ---------- */
function initRememberedAccount() {
  const form = document.getElementById("form-signin");
  if (!form) return;
  const emailInput = form.querySelector("input[name=email]");
  const password = form.querySelector("input[name=password]");
  let submittedEmail = "";

  form.addEventListener(
    "submit",
    () => {
      submittedEmail = emailInput ? emailInput.value.trim() : "";
    },
    true,
  );
  window.addEventListener(AUTH_EVENT, (e) => {
    if (!e.detail || e.detail.event !== "login_success") return;
    writeRememberedAccount(setLocalItem, { email: submittedEmail });
  });

  const account = readRememberedAccount(getLocalItem);
  if (!account || !emailInput) return;
  // Someone who just typed a different email elsewhere wants that account.
  if (carriedEmail && carriedEmail !== account.email) return;

  const show = (on) => {
    $("#lv2-head-default").hidden = on;
    $("#lv2-head-remembered").hidden = !on;
    $("#lv2-chip-wrap").hidden = !on;
    $("#lv2-email-field").hidden = on;
  };
  $("#lv2-chip-initials").textContent = accountInitials(account);
  $("#lv2-chip-name").textContent = account.email;
  emailInput.value = account.email;
  let chipOn = true;
  show(true);
  // Denominator for the PRD's chip metric: returning-device sessions.
  form.setAttribute("data-remembered-device", "true");
  trackAuth("remembered_account_shown", {});
  if (password) password.focus({ preventScroll: true });

  // local.js resets the form on some refusals (scheduled deletion,
  // verification required). The email field is hidden behind the chip, so
  // put the remembered address back or the next attempt has no email.
  form.addEventListener("reset", () => {
    window.setTimeout(() => {
      if (chipOn && !emailInput.value) emailInput.value = account.email;
    }, 0);
  });

  const notYou = $("#lv2-not-you");
  notYou.addEventListener("click", (e) => {
    e.preventDefault();
    clearRememberedAccount(removeLocalItem);
    trackAuth("remembered_account_cleared", {});
    chipOn = false;
    form.setAttribute("data-remembered-device", "false");
    emailInput.value = "";
    show(false);
    emailInput.focus();
  });
}

/* ---------- Forgot password: "Check your email" ---------- */
function initForgotSent() {
  const form = document.getElementById("form-forgot-password");
  if (!form) return;
  let sentTo = "";
  form.addEventListener(
    "submit",
    () => {
      const input = form.querySelector("input[name=email]");
      sentTo = input ? input.value.trim() : "";
    },
    true,
  );
  window.addEventListener(AUTH_EVENT, (e) => {
    if (!e.detail || e.detail.event !== "password_reset_requested") return;
    $("#lv2-forgot-sent-email").textContent = sentTo;
    $("#lv2-forgot-request").hidden = true;
    $("#lv2-forgot-sent").hidden = false;
    $("#lv2-forgot-sent-title").focus();
  });
}

/* ---------- Analytics on the frame ---------- */
function initAuthMethodTracking() {
  // auth_method_click is tracked for every arm in services/authEvents.js.
  const page = pageName();
  const changelog = document.getElementById("lv2-changelog-link");
  if (changelog) {
    changelog.addEventListener("click", () => {
      trackAuth("changelog_click", { link_url: changelog.href, page });
    });
  }
}

/* ---------- Arm A1: Switch carousel ---------- */
function initCarousel() {
  const panel = $(".lv2-panel--a1");
  if (!panel) return;
  const slides = $$(".lv2-sw-slide", panel);
  const dots = $$(".lv2-sw-dot", panel);
  const viewport = $(".lv2-sw-viewport", panel);
  const live = $("#lv2-sw-live");
  const nav = $(".lv2-sw-nav", panel);
  if (!slides.length || !viewport) return;
  nav.hidden = false;
  let current = 0;

  const go = (target, dir) => {
    const n = (target + slides.length) % slides.length;
    if (n === current) return;
    const from = slides[current];
    const to = slides[n];
    from.classList.remove("is-on", "from-left", "from-right");
    from.setAttribute("aria-hidden", "true");
    to.classList.remove("from-left", "from-right");
    void to.offsetWidth; // restart the entrance animation
    to.classList.add("is-on", dir < 0 ? "from-left" : "from-right");
    to.removeAttribute("aria-hidden");
    dots[current].classList.remove("is-on");
    dots[current].removeAttribute("aria-current");
    dots[n].classList.add("is-on");
    dots[n].setAttribute("aria-current", "true");
    current = n;
    const heading = $(".lv2-sw-h", to);
    if (live) {
      live.textContent = `Feature ${n + 1} of ${slides.length}: ${
        heading ? heading.textContent : ""
      }`;
    }
    trackAuth("switch_carousel_view", {
      slide: n + 1,
      feature: to.getAttribute("data-feature"),
    });
  };

  $$("[data-sw-step]", panel).forEach((btn) => {
    btn.addEventListener("click", () => {
      const step = Number(btn.getAttribute("data-sw-step"));
      go(current + step, step);
    });
  });
  dots.forEach((dot) => {
    dot.addEventListener("click", () => {
      const i = Number(dot.getAttribute("data-sw-go"));
      go(i, i < current ? -1 : 1);
    });
  });
  viewport.addEventListener("keydown", (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(current + 1, 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(current - 1, -1);
    }
  });
  let startX = null;
  viewport.addEventListener("pointerdown", (e) => {
    startX = e.clientX;
  });
  viewport.addEventListener("pointerup", (e) => {
    if (startX === null) return;
    const dx = e.clientX - startX;
    startX = null;
    if (Math.abs(dx) > 40) go(current + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
  });
  viewport.addEventListener("pointercancel", () => {
    startX = null;
  });
  // Later cards' mascots are lazy; warm them once the page is idle so the
  // first arrow press does not show an empty corner.
  if (!reducedMotion()) {
    window.setTimeout(() => {
      $$(".lv2-sw-mascot[loading=lazy]", panel).forEach((img) => {
        img.loading = "eager";
      });
    }, 1500);
  }
}

/* ---------- Arm A2: shortcut line ---------- */
function initShortcutLine() {
  const sub = document.getElementById("lv2-c-sub");
  if (!sub) return;
  const nav = window.navigator || {};
  const shortcut = switchShortcut({
    userAgent: nav.userAgent,
    platform:
      (nav.userAgentData && nav.userAgentData.platform) || nav.platform || "",
    maxTouchPoints: nav.maxTouchPoints,
    coarsePointer: !!(
      window.matchMedia && window.matchMedia("(pointer: coarse)").matches
    ),
  });
  if (!shortcut) return;
  const kbd = document.createElement("kbd");
  kbd.setAttribute("aria-label", shortcut.label);
  kbd.textContent = shortcut.keys;
  sub.appendChild(document.createTextNode(" Press "));
  sub.appendChild(kbd);
  sub.appendChild(document.createTextNode(" once you’re in."));
}

function init() {
  initReveal();
  initLinks();
  initRememberedAccount();
  initForgotSent();
  initAuthMethodTracking();
  initCarousel();
  initShortcutLine();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
